import React from "react";
import { NavbarIcon, LeftSidebarIcon, RightSidebarIcon, FooterIcon, PlusIcon, PencilIcon, XIcon } from "./icons";
import { NAVIGATION_TYPES, API_ENDPOINTS } from "./constants";
import { useEdit } from "../context/EditContext";
import { apiFetch } from "../../core/utils/api";
import { typography, surfaces, borders, textColors, brandColors } from "../../design-system/tokens";

const NavigationManagement = ({
  hasNavbar,
  hasLeftSidebar,
  hasRightSidebar,
  hasFooter = true, // Footer is always available
  websiteId,
  deleteConfirmation,
  setDeleteConfirmation,
  isManagingNav,
  navManagementError,
  setNavManagementError,
  setIsManagingNav,
  setNavManagementDropdownOpen
}) => {
  const { selectElement } = useEdit();

  const addNavigationElement = async (type) => {
    setIsManagingNav(true);
    setNavManagementError('');

    try {
      let endpoint, body;
      let enforcedType;
      
      switch (type) {
        case NAVIGATION_TYPES.NAVBAR:
          endpoint = API_ENDPOINTS.NAVBAR_UPDATE(websiteId);
          body = { section_position: 'left' };
          enforcedType = 'navbar';
          break;
        case NAVIGATION_TYPES.LEFT_SIDEBAR:
          endpoint = API_ENDPOINTS.LEFTSIDEBAR_UPDATE(websiteId);
          body = { side: 'left' };
          enforcedType = 'sidebar';
          break;
        case NAVIGATION_TYPES.RIGHT_SIDEBAR:
          endpoint = API_ENDPOINTS.RIGHTSIDEBAR_UPDATE(websiteId);
          body = { side: 'right' };
          enforcedType = 'sidebar';
          break;
        case NAVIGATION_TYPES.FOOTER:
          endpoint = API_ENDPOINTS.FOOTER_UPDATE(websiteId);
          body = {};
          enforcedType = 'footer';
          break;
        default:
          throw new Error('Invalid navigation type');
      }

      const response = await apiFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to add ${type}`);
      }

      // Avoid full browser reload: ask the editor + renderer to refetch the necessary data.
      try {
        const created = await response.json().catch(() => null);
        const elementType = created?.type || enforcedType;
        const elementId = created?.id || null;
        window.dispatchEvent(
          new CustomEvent("editor:refresh-website", {
            detail: { websiteId, reason: "navigation", elementType, elementId, source: "navigation-management:add" },
          }),
        );
      } catch (_) {
        window.dispatchEvent(
          new CustomEvent("editor:refresh-website", {
            detail: { websiteId, reason: "navigation", elementType: enforcedType, source: "navigation-management:add" },
          }),
        );
      }

    } catch (error) {
      setNavManagementError(error.message || 'Failed to add navigation');
    } finally {
      setIsManagingNav(false);
    }
  };

  const deleteNavigationElement = async (type) => {
    setIsManagingNav(true);
    setNavManagementError('');

    try {
      let endpoint;
      let enforcedType;
      
      switch (type) {
        case NAVIGATION_TYPES.NAVBAR:
          endpoint = API_ENDPOINTS.NAVBAR_DELETE(websiteId);
          enforcedType = 'navbar';
          break;
        case NAVIGATION_TYPES.LEFT_SIDEBAR:
          endpoint = API_ENDPOINTS.LEFTSIDEBAR_DELETE(websiteId);
          enforcedType = 'sidebar';
          break;
        case NAVIGATION_TYPES.RIGHT_SIDEBAR:
          endpoint = API_ENDPOINTS.RIGHTSIDEBAR_DELETE(websiteId);
          enforcedType = 'sidebar';
          break;
        default:
          throw new Error('Invalid navigation type for deletion');
      }

      const response = await apiFetch(endpoint, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (!response.ok) {
        const sidebarName = type === NAVIGATION_TYPES.LEFT_SIDEBAR ? 'left' : 'right';
        throw new Error(`Failed to delete ${type === NAVIGATION_TYPES.NAVBAR ? 'navbar' : `${sidebarName} sidebar`}`);
      }

      setDeleteConfirmation(null);
      // Avoid full browser reload: refetch website payload so nav elements appear/disappear immediately.
      try {
        window.dispatchEvent(
          new CustomEvent("editor:refresh-website", {
            detail: { websiteId, reason: "navigation", elementType: enforcedType, source: "navigation-management:delete" },
          }),
        );
      } catch (_) {}

    } catch (error) {
      setNavManagementError(error.message || 'Failed to delete navigation');
      setDeleteConfirmation(null);
    } finally {
      setIsManagingNav(false);
    }
  };

  const handleEdit = async (type) => {
    try {
      setIsManagingNav(true);
      setNavManagementError('');

      let endpoint;
      let enforcedType;
      switch (type) {
        case NAVIGATION_TYPES.NAVBAR:
          endpoint = API_ENDPOINTS.NAVBAR_GET(websiteId);
          enforcedType = 'navbar';
          break;
        case NAVIGATION_TYPES.LEFT_SIDEBAR:
          endpoint = API_ENDPOINTS.LEFTSIDEBAR_GET(websiteId);
          enforcedType = 'sidebar';
          break;
        case NAVIGATION_TYPES.RIGHT_SIDEBAR:
          endpoint = API_ENDPOINTS.RIGHTSIDEBAR_GET(websiteId);
          enforcedType = 'sidebar';
          break;
        case NAVIGATION_TYPES.FOOTER:
          endpoint = API_ENDPOINTS.FOOTER_GET(websiteId);
          enforcedType = 'footer';
          break;
        default:
          throw new Error('Invalid navigation type');
      }

      const res = await apiFetch(endpoint, { credentials: 'include' });
      if (!res.ok) {
        throw new Error('Failed to load element');
      }
      const data = await res.json();

      // Ensure type is present for EditContext selection
      const element = { ...data, type: data.type || enforcedType };

      await selectElement(element);
      setNavManagementDropdownOpen(false);

      // Optional smooth scroll to help user see the selected area
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {}
    } catch (error) {
      setNavManagementError(error.message || 'Failed to open editor');
    } finally {
      setIsManagingNav(false);
    }
  };

  const navigationItems = [
    {
      type: NAVIGATION_TYPES.NAVBAR,
      icon: NavbarIcon,
      label: 'Navbar',
      exists: hasNavbar,
      canDelete: false
    },
    {
      type: NAVIGATION_TYPES.LEFT_SIDEBAR,
      icon: LeftSidebarIcon,
      label: 'Left Sidebar',
      exists: hasLeftSidebar,
      canDelete: true
    },
    {
      type: NAVIGATION_TYPES.RIGHT_SIDEBAR,
      icon: RightSidebarIcon,
      label: 'Right Sidebar',
      exists: hasRightSidebar,
      canDelete: true
    },
    {
      type: NAVIGATION_TYPES.FOOTER,
      icon: FooterIcon,
      label: 'Footer',
      exists: hasFooter,
      canDelete: false // Footer cannot be deleted
    }
  ];

  return (
    <div className="p-4">
      <h3 className={`${typography.h4} ${textColors.primary} mb-4`}>Manage Navigation</h3>
      
      {navManagementError && (
        <div className={`mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md`}>
          <p className={`${typography.bodySmall} text-red-600 dark:text-red-400`}>{navManagementError}</p>
        </div>
      )}

      {deleteConfirmation && (
        <div className={`mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md`}>
          <h4 className={`${typography.bodySmall} ${textColors.primary} font-medium mb-2`}>
            Delete {deleteConfirmation === NAVIGATION_TYPES.NAVBAR ? 'Navbar' : 
                   deleteConfirmation === NAVIGATION_TYPES.LEFT_SIDEBAR ? 'Left Sidebar' : 'Right Sidebar'}?
          </h4>
          <p className={`${typography.bodySmall} ${textColors.secondary} mb-3`}>
            This will remove the navigation element and all its sections. This action cannot be undone.
          </p>
          <div className="flex space-x-2">
            <button
              onClick={() => deleteNavigationElement(deleteConfirmation)}
              disabled={isManagingNav}
              className={`px-3 py-1 bg-red-600 text-white ${typography.button} rounded hover:bg-red-700 disabled:opacity-50`}
            >
              {isManagingNav ? 'Deleting...' : 'Delete'}
            </button>
            <button
              onClick={() => setDeleteConfirmation(null)}
              className={`px-3 py-1 bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-300 ${typography.button} rounded hover:bg-gray-400 dark:hover:bg-gray-600`}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      <div className="space-y-4">
        {navigationItems.map(({ type, icon: IconComponent, label, exists, canDelete }) => (
          <div key={type} className={`flex items-center justify-between p-3 border ${borders.default} rounded-lg ${surfaces.hover} transition-colors`}>
            <div className="flex items-center">
              <div className={`mr-3 ${exists ? 'text-blue-600 dark:text-blue-400' : textColors.muted}`}>
              <IconComponent />
            </div>
            <div>
              <span className={`${typography.bodySmall} ${textColors.primary}`}>{label}</span>
            </div>
          </div>
            
            <div className="flex items-center space-x-2">
              {!exists ? (
                <button
                  onClick={() => addNavigationElement(type)}
                  disabled={isManagingNav || deleteConfirmation}
                  className={`p-2 ${textColors.secondary} hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 hover:dark:bg-blue-900/20 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                  title="Add"
                  aria-label={`Add ${label}`}
                >
                  {isManagingNav ? (
                    <div className={`w-5 h-5 animate-spin border-2 border-gray-400 dark:border-gray-600 border-t-transparent rounded-full`}></div>
                  ) : (
                    <PlusIcon />
                  )}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => handleEdit(type)}
                    disabled={isManagingNav || deleteConfirmation}
                    className={`p-2 ${textColors.secondary} ${textColors.hover} ${surfaces.hover} rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                    title="Edit"
                    aria-label={`Edit ${label}`}
                  >
                    <PencilIcon />
                  </button>
                  {canDelete && (
                    <button
                      onClick={() => setDeleteConfirmation(type)}
                      disabled={isManagingNav || deleteConfirmation}
                      className={`p-2 ${textColors.secondary} hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                      title="Delete"
                      aria-label={`Delete ${label}`}
                    >
                      <XIcon />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {!deleteConfirmation && (
      <div className="flex justify-end mt-6">
        <button
          onClick={() => setNavManagementDropdownOpen(false)}
          className={`px-4 py-2 ${typography.button} ${textColors.secondary} ${textColors.hover}`}
        >
          Close
        </button>
      </div>
      )}
    </div>
  );
};

export default NavigationManagement; 